import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button as AriaButton } from "react-aria-components";

import "@/features/store-overview/store-overview.css";

import closeIcon from "@/assets/figma/store-overview/close.svg";
import type {
  StoreOverviewDrawerSearch,
  StoreOverviewRangeKind,
} from "@/features/desktop-shell/desktopShellData";
import { isValidCustomRange } from "@/features/store-overview/storeOverviewPresentation";
import {
  useCloseTemporaryStoreOverviewConversationMutation,
  useCreateStoreOverviewReportMutation,
  useCreateTemporaryStoreOverviewConversationMutation,
  useGetStoreOverviewQuery,
  useLazyGetStoreOverviewReportQuery,
} from "@/features/store-overview/storeOverviewApi";
import {
  resolveStoreOverviewQueryView,
  STORE_OVERVIEW_UNAVAILABLE_FAILURE,
  type StoreOverviewQueryFailure,
} from "@/features/store-overview/storeOverviewQueryFailure";
import { StoreOverviewPanel } from "@/features/store-overview/StoreOverviewPanel";
import { TemporaryStoreOverviewChat } from "@/features/store-overview/TemporaryStoreOverviewChat";

interface StoreOverviewDrawerProps {
  onClose: () => void;
  onSearchChange: (update: Partial<StoreOverviewDrawerSearch>) => void;
  organizationId: string;
  search: StoreOverviewDrawerSearch;
  userId: string;
}

export interface StoreOverviewDrawerHandle {
  close: (afterClose: () => void) => void;
}

export const StoreOverviewDrawer = forwardRef<
  StoreOverviewDrawerHandle,
  StoreOverviewDrawerProps
>(function StoreOverviewDrawer(
  {
    onClose,
    onSearchChange,
    organizationId,
    search,
    userId,
  },
  ref,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reportIdempotencyKeysRef = useRef(new Map<string, string>());
  const reportPollAbortRef = useRef<AbortController | null>(null);
  const [closeError, setCloseError] = useState("");
  const [reportNotice, setReportNotice] = useState("");
  const hasValidCustomRange =
    search.range !== "custom" ||
    isValidCustomRange(search.customStart, search.customEnd);
  const overviewSkipped = !organizationId || !userId;
  const overviewQuery = useGetStoreOverviewQuery(
    {
      end: hasValidCustomRange ? search.customEnd : undefined,
      organizationId,
      range: hasValidCustomRange ? search.range : "today",
      start: hasValidCustomRange ? search.customStart : undefined,
      userId,
    },
    { skip: overviewSkipped },
  );
  const {
    failure: overviewFailure,
    overview,
    showLoading: showOverviewLoading,
  } = resolveStoreOverviewQueryView(overviewQuery, {
    skipped: overviewSkipped,
  });
  const [createTemporaryConversation, createTemporaryState] =
    useCreateTemporaryStoreOverviewConversationMutation();
  const [closeTemporaryConversation, closeTemporaryState] =
    useCloseTemporaryStoreOverviewConversationMutation();
  const [createReport, createReportState] =
    useCreateStoreOverviewReportMutation();
  const [getReport] = useLazyGetStoreOverviewReportQuery();

  useEffect(() => {
    return () => reportPollAbortRef.current?.abort();
  }, []);

  const closeChat = useCallback(
    async (afterClose?: () => void) => {
      if (!search.temporaryConversationId) {
        afterClose?.();
        return;
      }
      setCloseError("");
      try {
        await closeTemporaryConversation({
          conversationId: search.temporaryConversationId,
          organizationId,
          userId,
        }).unwrap();
        onSearchChange({ temporaryConversationId: "" });
        afterClose?.();
      } catch {
        setCloseError(
          "临时聊天仍可能在运行，服务端尚未确认删除。请稍后重试。",
        );
      }
    }, [
      closeTemporaryConversation,
      onSearchChange,
      organizationId,
      search.temporaryConversationId,
      userId,
    ],
  );

  const closeDrawer = useCallback(() => {
    if (search.temporaryConversationId) {
      void closeChat(onClose);
      return;
    }
    onClose();
  }, [closeChat, onClose, search.temporaryConversationId]);

  useImperativeHandle(
    ref,
    () => ({
      close: (afterClose) => {
        if (search.temporaryConversationId) {
          void closeChat(afterClose);
        } else {
          afterClose();
        }
      },
    }),
    [closeChat, search.temporaryConversationId],
  );

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      if (
        search.temporaryConversationId &&
        search.chatView === "open"
      ) {
        void closeChat();
      } else {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeChat, closeDrawer, search.chatView, search.temporaryConversationId]);

  useEffect(() => {
    if (!overview) return;
    rootRef.current
      ?.querySelector<HTMLElement>("#store-overview-title")
      ?.focus({ preventScroll: true });
  }, [overview]);

  const changeRange = (
    range: StoreOverviewRangeKind,
    customStart?: string,
    customEnd?: string,
  ) => {
    setReportNotice("");
    onSearchChange({
      ...(customEnd !== undefined ? { customEnd } : {}),
      ...(customStart !== undefined ? { customStart } : {}),
      range,
    });
  };

  const openTemporaryChat = async () => {
    if (search.temporaryConversationId) {
      onSearchChange({ chatView: "open" });
      return;
    }
    if (!overview) return;
    setCloseError("");
    try {
      const created = await createTemporaryConversation({
        organizationId,
        snapshotId: overview.snapshotId,
        userId,
      }).unwrap();
      if (created.snapshotId !== overview.snapshotId) {
        throw new Error("temporary_conversation_snapshot_identity_mismatch");
      }
      onSearchChange({
        chatView: "open",
        temporaryConversationId: created.conversationId,
      });
    } catch {
      setCloseError("临时聊天创建失败，当前快照可能已不可用，请重试。");
    }
  };

  const exportReport = async () => {
    if (!overview) return;
    setReportNotice("");
    try {
      const snapshotId = overview.snapshotId;
      const idempotencyKey =
        reportIdempotencyKeysRef.current.get(snapshotId) ?? crypto.randomUUID();
      reportIdempotencyKeysRef.current.set(snapshotId, idempotencyKey);
      const report = await createReport({
        idempotencyKey,
        organizationId,
        snapshotId,
        userId,
      }).unwrap();
      if (report.snapshotId !== snapshotId) {
        throw new Error("store_overview_report_snapshot_identity_mismatch");
      }
      if (report.status === "available" && report.resourceId) {
        setReportNotice("报告已生成，并已保存到组织云盘。");
      } else if (report.status === "failed") {
        reportIdempotencyKeysRef.current.delete(snapshotId);
        setReportNotice(`报告创建失败：${report.failureCode ?? "未知原因"}`);
      } else {
        setReportNotice("报告正在生成；文件可用后才会显示为已保存。");
        reportPollAbortRef.current?.abort();
        const controller = new AbortController();
        reportPollAbortRef.current = controller;
        void pollReport(report.jobId, snapshotId, controller);
      }
    } catch {
      setReportNotice("报告任务创建失败，请稍后重试。");
    }
  };

  const pollReport = async (
    jobId: string,
    snapshotId: string,
    controller: AbortController,
  ) => {
    try {
      for (let attempt = 0; attempt < 120; attempt += 1) {
        await abortableDelay(1_500, controller.signal);
        const report = await getReport(
          { jobId, organizationId, userId },
          false,
        ).unwrap();
        if (controller.signal.aborted) return;
        if (report.jobId !== jobId || report.snapshotId !== snapshotId) {
          reportIdempotencyKeysRef.current.delete(snapshotId);
          setReportNotice("报告状态与当前数据快照不一致，请重新创建。");
          return;
        }
        if (report.status === "available" && report.resourceId) {
          setReportNotice("报告已生成，并已保存到组织云盘。");
          return;
        }
        if (report.status === "failed") {
          reportIdempotencyKeysRef.current.delete(snapshotId);
          setReportNotice(`报告创建失败：${report.failureCode ?? "未知原因"}`);
          return;
        }
      }
      if (!controller.signal.aborted) {
        setReportNotice("报告仍在生成，可稍后到组织云盘查看。");
      }
    } catch {
      if (!controller.signal.aborted) {
        setReportNotice("报告状态查询暂时中断，可稍后到组织云盘查看。");
      }
    } finally {
      if (reportPollAbortRef.current === controller) {
        reportPollAbortRef.current = null;
      }
    }
  };

  const showChat = Boolean(
    search.temporaryConversationId && search.chatView === "open",
  );

  return (
    <div
      className="store-overview-drawer"
      data-chat-open={showChat || undefined}
      data-testid="store-overview-drawer"
      ref={rootRef}
    >
      <div className="store-overview-drawer__overview">
        {showOverviewLoading ? (
          <StoreOverviewLoading onClose={closeDrawer} />
        ) : overview ? (
          <StoreOverviewPanel
            customEnd={search.customEnd}
            customStart={search.customStart}
            isCreatingChat={createTemporaryState.isLoading}
            isCreatingReport={createReportState.isLoading}
            isTemporaryChatCollapsed={Boolean(
              search.temporaryConversationId && search.chatView === "collapsed",
            )}
            onClose={closeDrawer}
            onCreateReport={() => void exportReport()}
            onOpenTemporaryChat={() => void openTemporaryChat()}
            onRangeChange={changeRange}
            overview={overview}
            range={search.range}
            reportNotice={reportNotice}
          />
        ) : (
          <StoreOverviewError
            failure={overviewFailure ?? STORE_OVERVIEW_UNAVAILABLE_FAILURE}
            onClose={closeDrawer}
            onRetry={() => void overviewQuery.refetch()}
          />
        )}
      </div>

      {showChat ? (
        <TemporaryStoreOverviewChat
          conversationId={search.temporaryConversationId}
          isClosing={closeTemporaryState.isLoading}
          onClose={() => void closeChat()}
          onCollapse={() => onSearchChange({ chatView: "collapsed" })}
          organizationId={organizationId}
          userId={userId}
        />
      ) : null}

      {closeError ? (
        <div className="store-overview-drawer__delete-error" role="alert">
          <span>{closeError}</span>
          <AriaButton onPress={() => void closeChat()}>重试删除</AriaButton>
        </div>
      ) : null}
    </div>
  );
});

function abortableDelay(milliseconds: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      window.clearTimeout(timer);
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}

function StoreOverviewLoading({ onClose }: { onClose: () => void }) {
  return (
    <section
      aria-busy="true"
      aria-label="正在加载全店数据总览"
      className="store-overview-panel store-overview-panel--status"
      data-testid="store-overview-loading"
    >
      <AriaButton aria-label="关闭全店数据总览" className="store-overview-icon-button" onPress={onClose}>
        <img alt="" src={closeIcon} />
      </AriaButton>
      <div className="store-overview-loading__title" />
      <div className="store-overview-loading__kpis">
        {Array.from({ length: 5 }, (_, index) => <i key={index} />)}
      </div>
      <div className="store-overview-loading__cards">
        {Array.from({ length: 4 }, (_, index) => <i key={index} />)}
      </div>
      <span className="sr-only">正在读取服务端聚合快照</span>
    </section>
  );
}

function StoreOverviewError({
  failure,
  onClose,
  onRetry,
}: {
  failure: StoreOverviewQueryFailure;
  onClose: () => void;
  onRetry: () => void;
}) {
  const isForbidden = failure.kind === "forbidden";
  const isOwnerMissing = failure.kind === "owner_missing";
  return (
    <section className="store-overview-panel store-overview-panel--status" role="alert">
      <AriaButton aria-label="关闭全店数据总览" className="store-overview-icon-button" onPress={onClose}>
        <img alt="" src={closeIcon} />
      </AriaButton>
      <div className="store-overview-error">
        <strong>{isForbidden ? "没有查看全店数据的权限" : "全店数据暂时无法读取"}</strong>
        <p>
          {isForbidden
            ? "请联系组织管理员开通对应资源权限。"
            : isOwnerMissing
              ? "缺少组织或登录身份，请求未发出。没有使用缓存旧值冒充当前快照。"
              : "没有使用缓存旧值冒充当前快照。"}
        </p>
        {!isForbidden ? (
          <p>
            错误码：
            <code data-testid="store-overview-error-code">{failure.code}</code>
          </p>
        ) : null}
        {!isForbidden && !isOwnerMissing ? (
          <AriaButton onPress={onRetry}>重新加载</AriaButton>
        ) : null}
      </div>
    </section>
  );
}
